import type {
    CreatePaymentKassviaModel,
    CreatePaymentKassviaRequest,
    CreatePaymentRevolutModel,
    CreatePaymentRevolutRequest,
    CreatePaymentStripeModel,
    CreatePaymentStripeRequest,
    PaymentHistoryModel,
} from '@models';
import type { PaymentBonusModel } from '@models/payments/payment-bonus-model';
import type { PaymentGatewayModel } from '@models/payments/payment-gateway-model';
import type { PaymentListRequest } from '@models/payments/payment-list-request';
import type { PaginationListResponse } from '@models/types';

import { dashboardApi } from './dashboard-base-api';

const URL_PREFIX = 'webapi/v3/payments';

export const paymentApiSlice = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        getPaymentList: build.query<
            PaginationListResponse<PaymentHistoryModel>,
            PaymentListRequest
        >({
            query: ({
                params: {
                    organizationId,
                    page,
                    limit = 20,
                    created_from,
                    created_to,
                    paymentMethod,
                    status,
                },
            }) => ({
                url: `${URL_PREFIX}/${organizationId}/history-filters`,
                params: {
                    page,
                    limit,
                    ...(created_from && created_to && { created_from, created_to }),
                    paymentMethod,
                    status,
                },
            }),
        }),

        getPaymentBonuses: build.query<PaymentBonusModel[], string>({
            query: (organizationId) => ({
                url: `${URL_PREFIX}/${organizationId}/bonuses`,
            }),
        }),
        getPaymentGateways: build.query<PaymentGatewayModel[], string>({
            query: (organizationId) => ({
                url: `${URL_PREFIX}/${organizationId}/methods`,
            }),
            transformResponse: (response: { methods: PaymentGatewayModel[] }) => response?.methods,
        }),
        createPaymentRevolut: build.mutation<
            CreatePaymentRevolutModel,
            CreatePaymentRevolutRequest
        >({
            query: ({ body, params }) => ({
                method: 'POST',
                url: `${URL_PREFIX}/${params.organizationId}/checkout/revolut`,
                body,
            }),
        }),
        createPaymentStripe: build.mutation<CreatePaymentStripeModel, CreatePaymentStripeRequest>({
            query: ({ body, params }) => ({
                method: 'POST',
                url: `${URL_PREFIX}/${params.organizationId}/checkout/stripe`,
                body,
            }),
        }),
        createPaymentKassvia: build.mutation<
            CreatePaymentKassviaModel,
            CreatePaymentKassviaRequest
        >({
            query: ({ body, params }) => ({
                method: 'POST',
                url: `${URL_PREFIX}/${params.organizationId}/checkout/kassvia`,
                body,
            }),
        }),
        getPaymentKassviaInfo: build.query<
            PaymentHistoryModel,
            { organizationId: string; paymentId: string }
        >({
            query: ({ organizationId, paymentId }) =>
                `${URL_PREFIX}/${organizationId}/kassvia/${paymentId}`,
        }),
    }),
});

export const {
    useLazyGetPaymentListQuery,
    useGetPaymentListQuery,
    useGetPaymentGatewaysQuery,
    useGetPaymentBonusesQuery,
    useCreatePaymentRevolutMutation,
    useCreatePaymentKassviaMutation,
    useCreatePaymentStripeMutation,
    useLazyGetPaymentKassviaInfoQuery,
} = paymentApiSlice;
